import { Record } from 'immutable';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { preferencesV2Actions, preferencesV2Types } from 'app-components/flux/preferences-v2';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import * as actions from './preferencesV2Actions';
import { getLastSyncDate, getPreferencesV2 } from './preferencesV2Selectors';
import { isValidJSON, normalizeValue, transformResourceValue } from './preferencesV2Utils';

const mkResourceConfig = (props) =>
  resourceStoreTypes.mkQcsSyncResourceConfig({
    resourceName: 'preferences-v2',
    resourceBaseUrl: '/v2/preferences',
    getLastSyncDate,
    ...props,
  });

export function* getPreferencesV2ActionWatcher() {
  const config = mkResourceConfig({
    resourceBaseUrl: '/v2/preferences',
    successAction: actions.getPreferencesV2Success,
    failureAction: actions.getPreferencesV2Failure,
    // axiosConfig: { headers: { 'qcs-dataset-id': null } },
    // bc pref-v2 names are unique, we want to key off names for fast lookups
    transformResponseToResources: (response) =>
      response?.data?.resources?.map((resource) => {
        const respObj = {
          ...resource,
          id: resource?.name,
          preferenceId: resource?.id,
          value: normalizeValue(resource?.dataType, resource?.value),
        };
        return Record(respObj)(respObj);
      }),
  });
  yield takeEvery(actions.getPreferencesV2, resourceSagaUtils.qcsSyncGetResources, config);
}

export function* getPreferenceV2ActionWatcher() {
  const config = mkResourceConfig({
    resourceBaseUrl: '/v2/preferences',
    successAction: actions.getSpecificPreferenceV2Success,
    failureAction: actions.getSpecificPreferenceV2Failure,
    transformResponseToResource: ({ data }) =>
      preferencesV2Types.mkPreferenceV2({
        ...data,
        id: data?.name,
        preferenceId: data?.id,
        value: normalizeValue(data?.dataType, data?.value),
      }),
  });
  yield takeEvery(actions.getSpecificPreferenceV2, resourceSagaUtils.qcsSyncGetResource, config);
}

export function* updatePreferenceCriteriaActionWatcher() {
  const config = mkResourceConfig({
    resourceBaseUrl: '/v2/preferences/criteria',
    successAction: actions.updatePreferenceCriteriaSuccess,
    failureAction: actions.updatePreferenceCriteriaFailure,
  });
  yield takeEvery(actions.updatePreferenceCriteria, resourceSagaUtils.qcsSyncCreateResource, config);
}

function* updatePreferenceV2Saga(configuration, action) {
  const dataType = action?.payload?.dataType;
  let isValid = true;
  if (dataType && (dataType === 'JSON' || dataType === 'ARRAY')) {
    isValid = assert(isValidJSON(action?.payload?.value), 'invalid JSON pref value', action);
  }
  if (isValid) {
    yield call(resourceSagaUtils.qcsSyncCreateResource, configuration, action);
  }
}

export function* updatePreferenceOverrideActionWatcher() {
  const config = mkResourceConfig({
    resourceBaseUrl: () => '/v2/preferences/override',
    successAction: actions.updatePreferenceOverrideSuccess,
    failureAction: actions.updatePreferenceOverrideFailure,
    transformResourceWithResponse: null,
    // adding id here results in a 400 error due to override having a separate id not accessible to the client
    transformResourceToRequestData: (resource) => ({
      ...(resource?.toJS?.() || resource),
      value: transformResourceValue(resource),
      preferenceName: resource.preferenceName || resource.id,
      id: undefined,
    }),
  });
  yield takeEvery(actions.updatePreferenceOverride, updatePreferenceV2Saga, config);
}

export function* resetPreferenceOverrideActionWatcher() {
  yield takeEvery(actions.resetPreferenceOverride, function* resetPreferenceOverride(action) {
    yield call(resourceSagaUtils.qcsRequest, {
      method: 'POST',
      path: 'v2/preferences/reset-preference',
      data: { preferenceName: action.payload.id },
    });
    yield put(actions.getSpecificPreferenceV2({ id: action.payload.id }));
  });
}

export function* preferencesNudgeUpdater() {
  const bumpPreferencesV2 = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpPreferencesV2');
  if (bumpPreferencesV2) {
    yield put(preferencesV2Actions.getPreferencesV2(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

export function* preferencesDumpUpdater(resources) {
  const bumpPreferencesV2 = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpPreferencesV2');
  if (bumpPreferencesV2) {
    const prefs = yield select(getPreferencesV2);
    yield put(
      preferencesV2Actions.applyPreferenceChanges({
        doMerge: false,
        // TODO: remove filter once QCS deliver https://quickenteam.atlassian.net/browse/QCS-19834
        resources: resources
          .filter((resource) => prefs.get(resource.preferenceName)?.dataType)
          .map((resource) => {
            const pref = prefs.get(resource.preferenceName);
            return preferencesV2Types.mkPreferenceV2({
              ...(pref?.toJS?.() || pref),
              ...resource,
              id: resource.preferenceName,
              name: resource.preferenceName,
              value: normalizeValue(pref.dataType, resource.value), // TODO: use resouce.dataType filter once QCS deliver https://quickenteam.atlassian.net/browse/QCS-19834
            });
          }),
      }),
    );
  }
}

export default [
  getPreferencesV2ActionWatcher,
  getPreferenceV2ActionWatcher,
  resetPreferenceOverrideActionWatcher,
  updatePreferenceCriteriaActionWatcher,
  updatePreferenceOverrideActionWatcher,
];
